/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import { Column, GenericTable } from "../../components/GenericTable";
import { useMatchmakingService } from "../../services/backend/MatchmakingService";
import { Alert, AlertColor, Snackbar, MenuItem, TextField, Button } from "@mui/material";
import { AppContext } from "../../utils/AppContext";
import { LedIndicator, LedIndicatorColor } from "../../components/LedIndicator";
import { OpenInNew } from "@mui/icons-material";
import { getBaseURL } from "utils/Environment";

interface QueueType {
  id: string;
  name: string;
  envName: string;
  requestTimeoutSecs: number;
  rulesetName: string;
  description: string;
  webhookUrl: string;
  status: "active" | "disable";
  activate?: string;
}

const QueueConfigViewer = () => {
  const context = useContext(AppContext);

  const columns: Column<QueueType>[] = [
    {
      key: "status",
      label: "Status",
      type: "string",
      disableEdit: true,
      disableAdd: true,
      enabledSort: "asc",
      render: (value) =>
        value === "active" ? (
          <span role="img" aria-label="active">
            <LedIndicator color={LedIndicatorColor.GREEN} scale={0.2} />
          </span>
        ) : (
          <span role="img" aria-label="inactive">
            <LedIndicator color={LedIndicatorColor.GRAY} scale={0.2} />
          </span>
        ),
    },
    { key: "name", label: "Name", type: "string", enabledSort: "asc" },
    { key: "envName", label: "Environment Name", type: "string" },
    { key: "requestTimeoutSecs", label: "Request Timeout (secs)", type: "string", default: "600" },
    {
      key: "rulesetName",
      label: "Ruleset Name",
      type: "string",
      render: (value) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span>{value}</span>
          <OpenInNew
            style={{ cursor: "pointer", color: "#1976d2" }}
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/matchmaking/rulesets?rulesetName=${encodeURIComponent(String(value))}`, "_blank");
            }}
          />
        </div>
      ),
    },
    { key: "description", label: "Description", type: "string" },
    {
      key: "webhookUrl",
      label: "Webhook URL",
      type: "string",
      default: `${getBaseURL(context)}v1/matchmaking/webhook`,
    },

    {
      key: "activate",
      label: "Activate",
      type: "string",
      disableEdit: true,
      disableAdd: true,
      render: (_, row) => (
        <Button
          variant="outlined"
          color="primary"
          onClick={(e) => {
            e.stopPropagation();
            handleSetActive(row.id);
          }}
          disabled={row.status === "active"}
        >
          Set Active
        </Button>
      ),
    },
  ];

  const matchmakingService = useMatchmakingService();

  const [queueList, setQueueList] = useState<QueueType[]>([]);
  const [rulesetOptions, setRulesetOptions] = useState<string[]>([]);

  // Alerts
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");

  const fetchQueueList = async () => {
    try {
      const { queues } = await matchmakingService.listQueues();
      setQueueList(
        queues.map((queue: QueueType, index: number) => ({
          ...queue,
          id: queue.id || String(index),
        })),
      );
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of queues.");
      setAlertSeverity("error");
    }
  };

  const fetchRulesetOptions = async () => {
    try {
      const { ruleSets } = await matchmakingService.listRulesets();
      setRulesetOptions(ruleSets.map((ruleset: { name: string }) => ruleset.name));
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not fetch the list of rulesets.");
      setAlertSeverity("error");
    }
  };

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  const handleAddRow = async (newRow: QueueType) => {
    if (!/^[a-zA-Z0-9-]+$/.test(newRow.name)) {
      setAlertMessage("Name must match the pattern /a-zA-Z0-9-/.");
      setAlertSeverity("error");
      return;
    }

    await matchmakingService.createQueue(newRow);
    await fetchQueueList();
  };

  const handleEditRow = async (updatedRow: QueueType) => {
    const row = queueList.find((q) => q.id === updatedRow.id);

    if (!row) {
      setAlertMessage("Row not found");
      setAlertSeverity("error");
      return;
    }

    await matchmakingService.updateQueue(row.id, updatedRow);
    setQueueList((prevList) => prevList.map((q) => (q.id === updatedRow.id ? { ...q, ...updatedRow } : q)));
  };

  const handleDeleteRow = async (rowId: string) => {
    const row = queueList.find((q) => q.id === rowId);

    if (!row) {
      setAlertMessage("Row not found");
      setAlertSeverity("error");
      return;
    }

    await matchmakingService.deleteQueue(row.id);
    setQueueList((prevList) => prevList.filter((q) => q.id !== rowId));
  };

  const handleSetActive = async (rowId: string) => {
    try {
      const row = queueList.find((q) => q.id === rowId);

      if (!row) {
        setAlertMessage("Failed to get selected row");
        setAlertSeverity("error");
        return;
      }

      await matchmakingService.activateQueue({ name: "neighborhood", queueConfigurationId: row.id });
      setQueueList((prevList) =>
        prevList.map((q) => ({
          ...q,
          status: q.id === rowId ? "active" : "disable",
        })),
      );
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Could not update the row status.");
      setAlertSeverity("error");
    }
  };

  useEffect(() => {
    context?.setTitle("Matchmaking Queue Configuration");
    context?.setTargetAPI("environment");
    if (context?.selectedEnvironment) {
      (async () => {
        await fetchQueueList();
        await fetchRulesetOptions();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.selectedEnvironment]);

  return (
    <div>
      <GenericTable
        rows={queueList}
        columns={columns}
        enablePagination={true}
        enableSorting={true}
        onAddRow={handleAddRow}
        onEditRow={handleEditRow}
        onDeleteRow={handleDeleteRow}
        renderCustomInput={(key, value, handleInputChange) => {
          if (key === "rulesetName") {
            return (
              <TextField
                select
                label="Ruleset Name"
                value={value || ""}
                onChange={(e) => handleInputChange(key, e.target.value)}
                fullWidth
              >
                {rulesetOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            );
          }
          return null;
        }}
      />
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default QueueConfigViewer;
